import React, { useEffect, useMemo, useState } from "react";
import { Link, Typography, Box } from "@mui/material";
import type { Selected } from "../Context";
import { OccupancyState, colorMap } from "./icons/Occupancy";

type TrainPlateProps = {
  train: {
    lineId: string;
    routeId: string;
    active: boolean;
    lastUpdate: Date | null;
    occupancyLevel: OccupancyState | null;
  };
  selected: Selected | null;
  setSelected: (sel: Selected | null) => void;
};

// Bar length by level (categorical) — colour comes from the shared occupancy colorMap,
// so the sidebar bar always matches the train/carriage colours (route/live/occupancy_level).
const LEVEL_WIDTH: Record<OccupancyState, string> = {
  [OccupancyState.low]: '35%',
  [OccupancyState.medium]: '70%',
  [OccupancyState.high]: '100%',
};

export const TrainPlate = ({ train, selected, setSelected }: TrainPlateProps) => {
  const blinkTime = 30000;

  const baseColor = useMemo(() => (train.active ? "#ffff54" : "#ffffff"), [train.active]);

  const [color, setColor] = useState(baseColor);
  const [bold, setBold] = useState<"normal" | "bold">("normal");

  useEffect(() => {
    const eventTime = train.lastUpdate ?? new Date(0);
    const diff = Date.now() - eventTime.getTime();

    if (diff < blinkTime) {
      setColor("#FFA500");
      setBold("bold");
    } else {
      setColor(baseColor);
      setBold("normal");
    }

    const remaining = Math.max(0, blinkTime - diff);
    const t = window.setTimeout(() => {
      setColor(baseColor);
      setBold("normal");
    }, remaining);

    return () => window.clearTimeout(t);
  }, [train.lastUpdate, baseColor]);

  const isSelected =
    selected?.lineId === train.lineId && selected?.routeId === train.routeId;

  const level = train.occupancyLevel;
  const barColour = level ? colorMap[level] : 'rgba(255,255,255,0.1)';
  const barWidth  = level ? LEVEL_WIDTH[level] : '0%';

  return (
    <Box
      sx={{
        px: '4px', py: '6px',
        // Was background #163244 on a #18364a drawer — a ~2% difference, so the
        // selected train was effectively invisible. Needs to be obvious which
        // record the main view is showing.
        ...(isSelected ? {
          background: '#0f2a3d',
          borderLeft: '3px solid #f0a500',
          borderRadius: '2px',
          pl: '1px',
        } : { borderLeft: '3px solid transparent' }),
      }}
    >
      <Typography
        textAlign="center"
        fontWeight={isSelected ? 'bold' : bold}
        sx={{ fontSize: "12px", color: "#ffffff" }}
        noWrap
      >
        <Link
          color={color}
          underline="none"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setSelected({ lineId: train.lineId, routeId: train.routeId });
          }}
        >
          {train.routeId}
        </Link>
      </Typography>

      {/* Occupancy bar */}
      <Box sx={{ mt: '3px', height: '3px', width: '100%', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: barWidth, backgroundColor: barColour, borderRadius: '2px', transition: 'width 0.4s ease, background-color 0.4s ease' }} />
      </Box>
    </Box>
  );
};
